import React, {FC, useState} from 'react';
import styled from 'styled-components';
import {Alert, Input, Select} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import {authInfoSelect} from '@entities/auth';
import {ICard} from '@entities/card/types';
import {AppDispatch} from '@config/store';
import {clientSelect} from '@entities/client';
import {optionsCardSelect} from '@utils/const';
import {ECardAction} from '@utils';
import {cardSelect} from '@entities/card';
import {flexMixin, textMixin} from '@styles/mixins';
import {colors} from '@styles';
import {
    blockCardRequest,
    changeCardTemplateRequest,
    unlockCardRequest,
} from '@api/services/cardApi';
import {getClientCards} from '@api/thunks/card';

interface PropsClientModal {
    card: ICard;
    setShowEditCardModal: (val: boolean) => void;
}

const EditCardModal: FC<PropsClientModal> = ({card, setShowEditCardModal}) => {
    const {shop} = useSelector(authInfoSelect);
    const {selectedClient} = useSelector(clientSelect);
    const dispatch = useDispatch<AppDispatch>();
    const [selectValue, setSelectValue] = useState<number>(1);
    const [selectTemplate, setSelectTemplate] = useState<string>('');
    const [selectAction, setSelectAction] = useState<ECardAction>(
        ECardAction.ACTIVE
    );
    const [isError, setIsError] = useState<boolean>(false);
    const [message, setMessage] = useState<string>('');
    const [errMessage, setErrMessage] = useState<string>('Выберите макет');
    const {templates} = useSelector(cardSelect);
    const isChangeTemplate =
        optionsCardSelect[selectValue]?.label !== ECardAction.CHANGE_TEMPLATE;

    const handleSubmit = async () => {
        if (!shop || !selectedClient) return;

        try {
            if (selectAction === ECardAction.ACTIVE) {
                await unlockCardRequest(card.id, shop.id);
            }
            if (selectAction === ECardAction.BLOCK) {
                await blockCardRequest(card.id, shop.id, message);
            }
            if (selectAction === ECardAction.CHANGE_TEMPLATE) {
                if (!selectTemplate) {
                    setIsError(true);
                    return;
                }

                setIsError(false);
                await changeCardTemplateRequest(
                    card.id,
                    shop.id,
                    +selectTemplate
                );
            }

            dispatch(
                getClientCards({client_id: selectedClient.id, shop_id: shop.id})
            );
            setShowEditCardModal(false);
        } catch (err: any) {
            setErrMessage(err?.response?.data?.message || 'Error');
            setIsError(true);
        }
    };

    const onChangeSelect = (value: number) => {
        setSelectValue(value);
        setSelectAction(optionsCardSelect[value].label);
    };

    const onChangeTemplateSelect = (value: unknown) => {
        if (typeof value === 'string') {
            setSelectTemplate(value as unknown as string);
        }
    };

    return (
        <FormWrap>
            <HeaderTitle>Редактирование карты</HeaderTitle>
            <Br />
            <ActionWrap>
                <div>
                    <Label $isChangeTemplate={false}>Выберите операцию</Label>
                    <StyledSelect
                        getPopupContainer={(triggerNode) =>
                            triggerNode.parentNode
                        }
                        options={optionsCardSelect}
                        value={selectValue}
                        onChange={(val) => onChangeSelect(val as number)}
                    />
                </div>

                <div>
                    <Label $isChangeTemplate={isChangeTemplate}>
                        Выберите макет
                    </Label>
                    <StyledSelect
                        disabled={isChangeTemplate}
                        getPopupContainer={(triggerNode) =>
                            triggerNode.parentNode
                        }
                        options={templates?.map((item) => ({
                            label: item.name,
                            value: item.id.toString(),
                        }))}
                        value={selectTemplate}
                        onChange={(value: unknown) =>
                            onChangeTemplateSelect(value)
                        }
                    />
                </div>
            </ActionWrap>
            {isError && (
                <ErrorText>
                    <Alert showIcon message={errMessage} type="error" />
                </ErrorText>
            )}
            <Description
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={
                    optionsCardSelect[selectValue]?.label !== ECardAction.BLOCK
                }
                placeholder="Комментарии к действию"
            />
            <ButtonWrap>
                <CloseButton onClick={() => setShowEditCardModal(false)}>
                    Закрыть
                </CloseButton>
                <SaveButton onClick={handleSubmit}>Применить</SaveButton>
            </ButtonWrap>
        </FormWrap>
    );
};

export default EditCardModal;

const ErrorText = styled.div`
    margin-top: 10px;
`;

const ActionWrap = styled.div`
    ${flexMixin('column', 'center', 'flex-start')}
    gap: 10px;
`;
const StyledSelect = styled(Select)`
    box-sizing: border-box;
    ${flexMixin('row', 'center', 'center')}
    width: 175px;
    height: 38px;
    z-index: 1000;
`;

const HeaderTitle = styled.div`
    height: 23px;
    text-align: center;
    ${textMixin({
        fontSize: '18px',
        fontWeight: 700,
    })};
    line-height: 23px;
    ${flexMixin('row', 'flex-start', 'center')}
    color: ${colors.grey}
`;
const Br = styled.div`
    width: 298px;
    height: 0;
    margin: 20px 0 23px;
    border: 1px solid #eeefef;
`;

const Description = styled(Input)`
    margin-top: 13px;
    width: 298px;
    height: 92px;
    background: #eeefef;
    border-radius: 5px;
`;
const ButtonWrap = styled.div`
    ${flexMixin('row', 'space-between', 'center')}
    margin-top: 18px;
`;
const CloseButton = styled.div`
    width: 59px;
    height: 28px;
    ${textMixin({
        fontSize: '14px',
        fontWeight: 400,
    })};
    line-height: 28px;
    display: flex;
    align-items: center;
    text-align: center;
    color: #8f8f8f;
    cursor: pointer;
`;
const SaveButton = styled.button`
    ${flexMixin('row', 'center', 'center')}
    gap: 10px;
    border: none;
    width: 190px;
    height: 40px;
    cursor: pointer;
    background: #a7388e;
    border-radius: 5px;
    ${textMixin({
        fontSize: '14px',
        fontWeight: 400,
    })};
    color: ${colors.cleanWhite};
    line-height: 28px;
`;
const FormWrap = styled.div`
    display: flex;
    position: fixed;
    flex-direction: column;
    justify-content: flex-start;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
    background-color: ${colors.cleanWhite};
    padding: 29px 26px 22px;
    border-radius: 5px;
    width: 350px;
    min-height: 380px;
`;
const Label = styled.div<{$isChangeTemplate: boolean}>`
    ${textMixin({
        fontSize: '12px',
        fontWeight: 600,
    })};
    margin-bottom: 5px;
    color: ${({$isChangeTemplate}) =>
        $isChangeTemplate ? '#eeefef' : '#1f1e24'};
`;
